import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  DocumentTextIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  DocumentChartBarIcon,
  ChevronDownIcon,
  CheckIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

// ============================================
// CUSTOM DROPDOWN
// ============================================

const Dropdown = ({ value, onChange, options, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedOption = options.find(opt => opt.id === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between gap-3 px-4 py-3 
          bg-white border border-gray-300 rounded-lg shadow-sm text-left transition-all
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 cursor-pointer'}
          ${isOpen ? 'border-red-500 ring-2 ring-red-500/20' : ''}
        `}
      >
        <div className="flex items-center gap-3">
          {selectedOption?.icon && (
            <div className={`p-2 rounded-lg ${selectedOption.iconBg}`}>
              <selectedOption.icon className={`h-5 w-5 ${selectedOption.iconColor}`} />
            </div>
          )}
          <div>
            <p className="text-sm font-semibold text-gray-900">{selectedOption?.label}</p>
            <p className="text-xs text-gray-500">{selectedOption?.description}</p>
          </div>
        </div>
        <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {options.map((option) => {
            const isSelected = option.id === value;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => { onChange(option.id); setIsOpen(false); }}
                className={`
                  w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors
                  ${isSelected ? 'bg-red-50' : 'hover:bg-gray-50'}
                `}
              >
                <div className="flex items-center gap-3">
                  {option.icon && (
                    <div className={`p-2 rounded-lg ${option.iconBg}`}>
                      <option.icon className={`h-5 w-5 ${option.iconColor}`} />
                    </div>
                  )}
                  <div>
                    <p className={`text-sm font-semibold ${isSelected ? 'text-red-900' : 'text-gray-900'}`}>
                      {option.label}
                    </p>
                    <p className="text-xs text-gray-500">{option.description}</p>
                  </div>
                </div>
                {isSelected && <CheckIcon className="h-5 w-5 text-red-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================
// CONFIGURATION
// ============================================

const EXPORT_TYPES = [
  {
    id: 'ames',
    label: 'Ames Report',
    description: 'Session summary with IRI data and events',
    icon: DocumentChartBarIcon,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
  },
  // {
  //   id: 'proval',
  //   label: 'ProVAL Exports',
  //   description: 'DOT-compatible data files (SAM/RQ modules)',
  //   icon: DocumentTextIcon,
  //   iconBg: 'bg-blue-100',
  //   iconColor: 'text-blue-600',
  // },
];

const AMES_FORMATS = [
  { id: 'pdf', label: 'PDF', description: 'Best for sharing' },
  { id: 'docx', label: 'Word', description: 'Editable document' },
  { id: 'xlsx', label: 'Excel', description: 'Spreadsheet format' },
  { id: 'ames-legacy', label: 'Legacy TXT', description: 'Ames profiler format' },
];

const SAM_EXPORTS = [
  { id: 'sam-fixed', label: 'Fixed Interval', description: 'Segment IRI values (Left/Right/MRI)' },
  { id: 'sam-defects-short', label: 'Defects - Short Continuous', description: '25ft ALR events by wheelpath' },
  { id: 'sam-defects-long', label: 'Defects - Long Continuous', description: 'Segments exceeding IRI threshold', usesThreshold: true },
];

const RQ_EXPORTS = [
  { id: 'rq-fixed', label: 'Fixed Interval', description: 'Segment IRI for pay adjustment' },
];

// ============================================
// MAIN COMPONENT
// ============================================

const ReportGeneratorModal = ({
  isOpen,
  onClose,
  session,
  project,
  projectConfig,
  segments,
  events,
  onGenerate
}) => {
  const [exportType, setExportType] = useState('ames');
  const [amesFormat, setAmesFormat] = useState('pdf');
  const [selectedExports, setSelectedExports] = useState([]);
  const [provalFormat, setProvalFormat] = useState('txt');
  const defaultThreshold = projectConfig?.iri_threshold_type_a || 160;
  const [threshold, setThreshold] = useState(defaultThreshold);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const sessionName = session?.name || session?.filename || 'Session';
  const showThresholdConfig = exportType === 'proval' && selectedExports.includes('sam-defects-long');
  const canGenerate = exportType === 'ames' || selectedExports.length > 0;

  const handleExportToggle = (exportId) => {
    setSelectedExports(prev =>
      prev.includes(exportId) ? prev.filter(id => id !== exportId) : [...prev, exportId]
    );
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);

    try {
      const options = {
        exportType,
        session,
        project,
        projectConfig,
        segments,
        events,
      };

      if (exportType === 'ames') {
        options.amesFormat = amesFormat;
      } else {
        options.provalExports = selectedExports.map(id => ({
          id,
          format: provalFormat,
          threshold,
        }));
      }

      await onGenerate(options);
      onClose();
    } catch (err) {
      console.error('Generation failed:', err);
      setError(err.message || 'Failed to generate');
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    if (!generating) {
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Generate Report</h3>
            <p className="text-sm text-gray-500 truncate max-w-xs">{sessionName}</p>
          </div>
          <button
            onClick={handleClose}
            disabled={generating}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          <Dropdown
            value={exportType}
            onChange={setExportType}
            options={EXPORT_TYPES}
            disabled={generating}
          />

          {/* Ames Options */}
          {exportType === 'ames' && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Format</label>
              <div className="grid grid-cols-2 gap-2">
                {AMES_FORMATS.map(format => (
                  <button
                    key={format.id}
                    onClick={() => setAmesFormat(format.id)}
                    disabled={generating}
                    className={`
                      px-3 py-3 rounded-lg border-2 text-center transition-all
                      ${amesFormat === format.id
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'}
                      disabled:opacity-50
                    `}
                  >
                    <p className={`text-sm font-medium ${amesFormat === format.id ? 'text-red-900' : 'text-gray-900'}`}>
                      {format.label}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{format.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ProVAL Options */}
          {exportType === 'proval' && (
            <div className="space-y-5">

              {/* SAM Section */}
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Smoothness Assurance (SAM)
                </h4>
                <div className="space-y-1">
                  {SAM_EXPORTS.map(exp => {
                    const isSelected = selectedExports.includes(exp.id);
                    return (
                      <label
                        key={exp.id}
                        className={`
                          flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors
                          ${isSelected ? 'bg-red-50 border border-red-200' : 'hover:bg-gray-50 border border-transparent'}
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleExportToggle(exp.id)}
                          disabled={generating}
                          className="mt-0.5 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isSelected ? 'text-red-900' : 'text-gray-900'}`}>
                            {exp.label}
                          </p>
                          <p className="text-xs text-gray-500">{exp.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* RQ Section */}
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Ride Quality (RQ)
                </h4>
                <div className="space-y-1">
                  {RQ_EXPORTS.map(exp => {
                    const isSelected = selectedExports.includes(exp.id);
                    return (
                      <label
                        key={exp.id}
                        className={`
                          flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors
                          ${isSelected ? 'bg-red-50 border border-red-200' : 'hover:bg-gray-50 border border-transparent'}
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleExportToggle(exp.id)}
                          disabled={generating}
                          className="mt-0.5 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isSelected ? 'text-red-900' : 'text-gray-900'}`}>
                            {exp.label}
                          </p>
                          <p className="text-xs text-gray-500">{exp.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Threshold Config */}
              {showThresholdConfig && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Cog6ToothIcon className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-900">IRI Threshold</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      value={threshold}
                      onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
                      disabled={generating}
                      className="w-24 px-3 py-1.5 text-sm border border-amber-300 rounded-md focus:ring-amber-500 focus:border-amber-500 bg-white"
                      step="1"
                      min="0"
                    />
                    <span className="text-sm text-amber-800">in/mi</span>
                    <span className="text-xs text-amber-600 ml-auto">Default: {defaultThreshold}</span>
                  </div>
                </div>
              )}

              {/* Output Format Toggle */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Output Format</span>
                  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setProvalFormat('txt')}
                      disabled={generating}
                      className={`
                        px-4 py-2 text-sm font-medium transition-colors
                        ${provalFormat === 'txt' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}
                        disabled:opacity-50
                      `}
                    >
                      TXT
                    </button>
                    <button
                      type="button"
                      onClick={() => setProvalFormat('csv')}
                      disabled={generating}
                      className={`
                        px-4 py-2 text-sm font-medium transition-colors border-l border-gray-300
                        ${provalFormat === 'csv' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}
                        disabled:opacity-50
                      `}
                    >
                      CSV
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            {exportType === 'proval' && selectedExports.length > 0 && (
              <span>{selectedExports.length} file{selectedExports.length !== 1 ? 's' : ''} selected</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={generating}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating || !canGenerate}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Generate
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReportGeneratorModal;